// src/reactn/index.js
import * as React from "react";
import { createGlobalState } from "react-hooks-global-state";

//
// 1️⃣ Define the global state shape
//
const initialState = {
  currentUser: null,
  registeredPrograms: null, // ✅ consistent spelling
  programs: [],
  currentProgram: null,
  amountPaid: null,

  currentJob: null,
  currentProgram: null,
  selectedSubjects: [],
  currentSubject: null,
};

//
// 2️⃣ Create the global hooks
//
const { useGlobalState, setGlobalState, getGlobalState } =
  createGlobalState(initialState);

//
// 3️⃣ ReactN-compatible API
//
export const useGlobal = (key) => {
  const [value, setValue] = useGlobalState(key);

  // Wrap setValue to support a callback
  const setValueWithCallback = (newValue, callback) => {
    setValue(newValue);
    if (typeof callback === "function") {
      // Ensure callback runs *after* state update & re-render
      Promise.resolve().then(callback);
    }
  };

  return [value, setValueWithCallback];
};

export const setGlobal = (update, callback) => {
  if (typeof update === "function") {
    const current = getGlobalState();
    const next = update(current);
    if (next && typeof next === "object") {
      Object.entries(next).forEach(([key, value]) =>
        setGlobalState(key, value)
      );
    }
  } else if (typeof update === "object" && update !== null) {
    Object.entries(update).forEach(([key, value]) =>
      setGlobalState(key, value)
    );
  } else {
    console.warn("⚠️ setGlobal expects an object or updater function");
  }

  if (typeof callback === "function") {
    Promise.resolve().then(callback);
  }
};

//
// 4️⃣ LocalStorage persistence hook
//
const PERSIST_KEY = `@aspire-${process.env.NODE_ENV}`;
const WHITELIST = [
  "currentUser",
  "programs",
  "registeredPrograms",
  "amountPaid",
  "currentJob",
  "currentProgram",
  "selectedSubjects",
  "currentSubject",
];

export const usePersistentState = () => {
  // Track only whitelisted keys individually
  const [currentUser] = useGlobal("currentUser");
  const [programs] = useGlobal("programs");
  const [registeredPrograms] = useGlobal("registeredPrograms");
  const [amountPaid] = useGlobal("amountPaid");

  // Load persisted data on mount
  React.useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PERSIST_KEY));
      if (saved && typeof saved === "object") {
        Object.entries(saved).forEach(([key, value]) => {
          if (WHITELIST.includes(key) && key in initialState)
            setGlobalState(key, value);
        });
      }
    } catch (err) {
      console.warn("🧹 Failed to load persisted state, clearing it:", err);
      localStorage.removeItem(PERSIST_KEY);
    }
  }, []);

  // Persist to storage whenever relevant keys change
  React.useEffect(() => {
    try {
      const current = getGlobalState();
      const filtered = Object.fromEntries(
        Object.entries(current).filter(([key]) => WHITELIST.includes(key))
      );
      localStorage.setItem(PERSIST_KEY, JSON.stringify(filtered));
    } catch (err) {
      console.warn("⚠️ Failed to save global state:", err);
    }
  }, [currentUser, programs, registeredPrograms, amountPaid]);
};

//
// 5️⃣ Export everything from React for convenience
//
export * from "react";
export default React;
